import { useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";

/**
 * Wraps a server function so that calls automatically attach the user's
 * Supabase access token in the Authorization header (required by the
 * `requireSupabaseAuth` middleware).
 *
 * Works by patching `window.fetch` for the duration of the call and adding
 * the Authorization header to requests targeting `/_serverFn/...`.
 */
export function useAuthedServerFn<TFn extends (...args: any[]) => Promise<any>>(
  fn: TFn,
): TFn {
  const serverFn = useServerFn(fn);

  return useCallback(
    async (...args: Parameters<TFn>) => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;

      if (!token || typeof window === "undefined") {
        return serverFn(...args);
      }

      const originalFetch = window.fetch;
      window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url;

        if (url.includes("/_serverFn/")) {
          const headers = new Headers(init?.headers);
          if (!headers.has("authorization")) {
            headers.set("authorization", `Bearer ${token}`);
          }
          return originalFetch(input, { ...init, headers });
        }
        return originalFetch(input, init);
      }) as typeof fetch;

      try {
        return await serverFn(...args);
      } finally {
        window.fetch = originalFetch;
      }
    },
    [serverFn],
  ) as TFn;
}
